/**
 * SchoolSafeguarding — Sensitive complaint + case management.
 * Role-gated UI. Every access logged.
 * Props: tenantId
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldAlert, Plus, Lock, Eye, FileWarning, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import api from '@/lib/api';

const BASE = (tid) => `/school/tenants/${tid}/safeguarding`;

const SENSITIVE_ROLES = new Set(['super_admin', 'owner', 'principal', 'hr_admin', 'safeguarding_officer', 'admin']);

const RISK_COLORS = {
  low: 'bg-slate-100 text-slate-700 border-slate-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  high: 'bg-orange-100 text-orange-800 border-orange-400',
  critical: 'bg-rose-100 text-rose-800 border-rose-500',
};
const STATUS_COLORS = {
  submitted: 'bg-sky-100 text-sky-800',
  under_review: 'bg-amber-100 text-amber-800',
  safeguarding_review: 'bg-rose-100 text-rose-800',
  hr_review: 'bg-indigo-100 text-indigo-800',
  investigation_ongoing: 'bg-purple-100 text-purple-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  escalated_externally: 'bg-red-100 text-red-900',
  closed: 'bg-slate-100 text-slate-600',
  open: 'bg-sky-100 text-sky-800',
  investigation: 'bg-purple-100 text-purple-800',
  escalated: 'bg-rose-100 text-rose-800',
};

const SchoolSafeguarding = ({ tenantId }) => {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const role = (user?.role || '').toLowerCase();
  const isAuthorized = SENSITIVE_ROLES.has(role) || user?.is_super_admin;

  const [tab, setTab] = useState('complaints');
  const [newComplaintOpen, setNewComplaintOpen] = useState(false);
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [openComplaint, setOpenComplaint] = useState(null);
  const [openCase, setOpenCase] = useState(null);

  const { data: stats } = useQuery({
    queryKey: ['sg-stats', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/stats`)).data,
    enabled: isAuthorized,
  });
  const { data: catalog } = useQuery({
    queryKey: ['sg-catalog', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/catalog`)).data,
    enabled: isAuthorized,
  });
  const { data: complaints = [] } = useQuery({
    queryKey: ['sg-complaints', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/complaints`)).data,
    enabled: isAuthorized,
  });
  const { data: cases = [] } = useQuery({
    queryKey: ['sg-cases', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/cases`)).data,
    enabled: isAuthorized,
  });

  if (!isAuthorized) {
    return (
      <div className="max-w-md mx-auto mt-10 text-center" data-testid="safeguarding-restricted">
        <Lock className="h-12 w-12 text-rose-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold">Restricted Area</h3>
        <p className="text-slate-500 text-sm mt-2">
          Safeguarding & complaints are only accessible to authorized roles
          (Principal, HR Admin, Safeguarding Officer). Your role ({role || 'unknown'}) does not have access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="school-safeguarding-page">
      <div className="bg-gradient-to-r from-rose-50 to-rose-100 border-l-4 border-rose-500 p-4 rounded flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-rose-900">Confidential area</p>
          <p className="text-rose-800 text-xs mt-1">
            Every view, edit, and status change is audit-logged. Share records only with authorized personnel.
            Safeguarding concerns affecting immediate danger must be escalated to safeguarding lead immediately.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-rose-600" /> Safeguarding & Complaints
        </h2>
        <div className="flex gap-2">
          <Button onClick={() => setNewComplaintOpen(true)} variant="outline" data-testid="sg-new-complaint-btn">
            <Plus className="h-4 w-4 mr-1" /> New Complaint
          </Button>
          <Button onClick={() => setNewCaseOpen(true)} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="sg-new-case-btn">
            <FileWarning className="h-4 w-4 mr-1" /> New Case
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Complaints" value={stats?.complaints_total ?? 0} color="slate" testid="sg-stat-total" />
        <StatCard label="Open Complaints" value={stats?.complaints_open ?? 0} color="amber" testid="sg-stat-open" />
        <StatCard label="Safeguarding Flagged" value={stats?.safeguarding_flagged_open ?? 0} color="rose" testid="sg-stat-flagged" />
        <StatCard label="Open Cases" value={stats?.cases_open ?? 0} color="indigo" testid="sg-stat-cases" />
        <StatCard label="High-Risk Cases" value={stats?.cases_high_risk ?? 0} color="red" testid="sg-stat-high-risk" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="complaints" data-testid="sg-tab-complaints">Complaints</TabsTrigger>
          <TabsTrigger value="cases" data-testid="sg-tab-cases">Cases</TabsTrigger>
          <TabsTrigger value="repeat" data-testid="sg-tab-repeat">Repeat Staff Flags</TabsTrigger>
        </TabsList>

        <TabsContent value="complaints" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">Number</th>
                    <th className="text-left p-3">Subject</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Flags</th>
                    <th className="text-left p-3">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {complaints.length === 0 && <tr><td colSpan={6} className="p-5 text-center text-slate-500">No complaints.</td></tr>}
                  {complaints.map(c => (
                    <tr key={c.id} className="border-b cursor-pointer hover:bg-slate-50" onClick={() => setOpenComplaint(c.id)}
                        data-testid={`sg-complaint-row-${c.id}`}>
                      <td className="p-3 font-mono text-xs">{c.number}</td>
                      <td className="p-3 font-semibold">{c.subject}</td>
                      <td className="p-3 capitalize text-xs">{c.category?.replace(/_/g, ' ')}</td>
                      <td className="p-3"><Badge variant="outline" className={`capitalize ${STATUS_COLORS[c.status]}`}>{c.status?.replace(/_/g, ' ')}</Badge></td>
                      <td className="p-3 space-x-1">
                        {c.safeguarding_flag && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-300">SG</Badge>}
                        {c.immediate_danger && <Badge variant="outline" className="text-[10px] bg-red-100 text-red-800 border-red-400">URGENT</Badge>}
                        {c.anonymous && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-300">Anon</Badge>}
                      </td>
                      <td className="p-3 text-xs text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cases" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">Number</th>
                    <th className="text-left p-3">Title</th>
                    <th className="text-left p-3">Risk</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Owner</th>
                    <th className="text-left p-3">Review Date</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.length === 0 && <tr><td colSpan={6} className="p-5 text-center text-slate-500">No cases.</td></tr>}
                  {cases.map(c => (
                    <tr key={c.id} className="border-b cursor-pointer hover:bg-slate-50" onClick={() => setOpenCase(c.id)}
                        data-testid={`sg-case-row-${c.id}`}>
                      <td className="p-3 font-mono text-xs">{c.number}</td>
                      <td className="p-3 font-semibold">{c.title}</td>
                      <td className="p-3"><Badge variant="outline" className={`capitalize ${RISK_COLORS[c.risk_level]}`}>{c.risk_level}</Badge></td>
                      <td className="p-3"><Badge variant="outline" className={`capitalize ${STATUS_COLORS[c.status]}`}>{c.status}</Badge></td>
                      <td className="p-3 text-xs">{c.owner_email || '—'}</td>
                      <td className="p-3 text-xs text-slate-500">{c.review_date ? new Date(c.review_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="repeat" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b text-xs uppercase text-slate-500">
                  <tr><th className="text-left p-3">Staff</th><th className="text-right p-3">Complaint Count</th></tr>
                </thead>
                <tbody>
                  {(stats?.repeat_staff || []).length === 0 && <tr><td colSpan={2} className="p-5 text-center text-slate-500">No staff with repeat complaints.</td></tr>}
                  {(stats?.repeat_staff || []).map(s => (
                    <tr key={s.staff_id} className="border-b">
                      <td className="p-3">{s.name}</td>
                      <td className="p-3 text-right"><Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">{s.count}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewComplaintDialog open={newComplaintOpen} onOpenChange={setNewComplaintOpen}
                         tenantId={tenantId} catalog={catalog}
                         onCreated={() => qc.invalidateQueries({ queryKey: ['sg-complaints', tenantId] })} />
      <NewCaseDialog open={newCaseOpen} onOpenChange={setNewCaseOpen} tenantId={tenantId}
                    onCreated={() => qc.invalidateQueries({ queryKey: ['sg-cases', tenantId] })} />
      {openComplaint && <ComplaintDetailDialog tenantId={tenantId} id={openComplaint} onClose={() => setOpenComplaint(null)} catalog={catalog} />}
      {openCase && <CaseDetailDialog tenantId={tenantId} id={openCase} onClose={() => setOpenCase(null)} />}
    </div>
  );
};

const StatCard = ({ label, value, color, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-4 pb-3">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-3xl font-bold">{value}</p>
    </CardContent>
  </Card>
);

const NewComplaintDialog = ({ open, onOpenChange, tenantId, catalog, onCreated }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    subject: '', narrative: '', category: 'inappropriate_language',
    against_staff_name: '', location: '', safeguarding_flag: false, immediate_danger: false, anonymous: false,
  });
  const mut = useMutation({
    mutationFn: (data) => api.post(`${BASE(tenantId)}/complaints`, data).then(r => r.data),
    onSuccess: () => { toast.success('Complaint recorded'); onOpenChange(false); qc.invalidateQueries({ queryKey: ['sg-complaints', tenantId] }); qc.invalidateQueries({ queryKey: ['sg-stats', tenantId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileWarning className="h-5 w-5 text-rose-600" /> New Complaint</DialogTitle>
          <DialogDescription>Record a complaint or concern (confidential)</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject *</Label>
            <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="sg-complaint-subject" />
          </div>
          <div><Label>Category *</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="sg-complaint-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(catalog?.complaint_categories || []).map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Staff name (if any)</Label>
            <Input value={form.against_staff_name} onChange={e => setForm({ ...form, against_staff_name: e.target.value })} />
          </div>
          <div><Label>Narrative *</Label>
            <textarea className="w-full border rounded p-2 text-sm min-h-[110px]"
                      value={form.narrative} onChange={e => setForm({ ...form, narrative: e.target.value })}
                      data-testid="sg-complaint-narrative" />
          </div>
          <div><Label>Location</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.safeguarding_flag} onCheckedChange={v => setForm({ ...form, safeguarding_flag: !!v })}
                        data-testid="sg-complaint-safeguarding-flag" />
              <span>Safeguarding concern (child protection)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.immediate_danger} onCheckedChange={v => setForm({ ...form, immediate_danger: !!v })} />
              <span className="font-semibold text-rose-700">Immediate danger (triggers urgent escalation)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.anonymous} onCheckedChange={v => setForm({ ...form, anonymous: !!v })} />
              <span>Record as anonymous (reporter identity hidden)</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)}
                  disabled={mut.isPending || !form.subject || !form.narrative}
                  className="bg-rose-600 text-white" data-testid="sg-complaint-submit">
            {mut.isPending ? 'Filing...' : 'File Complaint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const NewCaseDialog = ({ open, onOpenChange, tenantId, onCreated }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ title: '', summary: '', risk_level: 'medium', review_date: '' });
  const mut = useMutation({
    mutationFn: (data) => api.post(`${BASE(tenantId)}/cases`, data).then(r => r.data),
    onSuccess: () => { toast.success('Case opened'); onOpenChange(false); qc.invalidateQueries({ queryKey: ['sg-cases', tenantId] }); qc.invalidateQueries({ queryKey: ['sg-stats', tenantId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New Safeguarding Case</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="sg-case-title" />
          </div>
          <div><Label>Summary *</Label>
            <textarea className="w-full border rounded p-2 text-sm min-h-[80px]"
                      value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })}
                      data-testid="sg-case-summary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Risk Level</Label>
              <Select value={form.risk_level} onValueChange={v => setForm({ ...form, risk_level: v })}>
                <SelectTrigger data-testid="sg-case-risk"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'medium', 'high', 'critical'].map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Review Date</Label>
              <Input type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value ? e.target.value + 'T00:00:00Z' : '' })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending || !form.title || !form.summary}
                  className="bg-rose-600 text-white" data-testid="sg-case-submit">
            {mut.isPending ? 'Opening...' : 'Open Case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ComplaintDetailDialog = ({ tenantId, id, onClose, catalog }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['sg-complaint-detail', id],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/complaints/${id}`)).data,
  });
  const { data: accessLog = [] } = useQuery({
    queryKey: ['sg-complaint-access', id],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/complaints/${id}/access-log`)).data,
  });
  const [status, setStatus] = useState('under_review');
  const [note, setNote] = useState('');
  const statusMut = useMutation({
    mutationFn: () => api.put(`${BASE(tenantId)}/complaints/${id}/status`, { status, note }),
    onSuccess: () => { toast.success(`Status: ${status}`); qc.invalidateQueries({ queryKey: ['sg-complaint-detail', id] }); qc.invalidateQueries({ queryKey: ['sg-complaints', tenantId] }); setNote(''); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {isLoading ? <p className="p-5">Loading...</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs">{data?.number}</span>
                <span>{data?.subject}</span>
                <Badge variant="outline" className={`capitalize ${STATUS_COLORS[data?.status]}`}>{data?.status?.replace(/_/g, ' ')}</Badge>
                {data?.safeguarding_flag && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">SG</Badge>}
                {data?.immediate_danger && <Badge variant="outline" className="bg-red-100 text-red-800 border-red-400 animate-pulse">URGENT</Badge>}
              </DialogTitle>
              <DialogDescription className="capitalize">{data?.category?.replace(/_/g, ' ')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-3">
                <p className="text-xs text-slate-500 mb-1">Narrative</p>
                <p className="text-sm whitespace-pre-wrap">{data?.narrative}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
                <p><b>Against:</b> {data?.against_staff_name || '—'}</p>
                <p><b>Location:</b> {data?.location || '—'}</p>
                <p><b>Reporter:</b> {data?.anonymous ? 'Anonymous' : data?.submitted_by_email}</p>
                <p><b>Filed:</b> {data?.created_at ? new Date(data.created_at).toLocaleString() : ''}</p>
              </div>

              <div className="border-t pt-3">
                <Label>Change status</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(catalog?.complaint_statuses || []).map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Optional note" value={note} onChange={e => setNote(e.target.value)} className="flex-1" />
                  <Button onClick={() => statusMut.mutate()} disabled={statusMut.isPending}
                          className="bg-indigo-600 text-white" data-testid="sg-complaint-status-submit">Apply</Button>
                </div>
              </div>

              <div className="border-t pt-3">
                <h4 className="text-sm font-semibold flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Access Audit ({accessLog.length})</h4>
                <div className="space-y-1 text-[11px] mt-2 max-h-40 overflow-y-auto">
                  {accessLog.map(l => (
                    <div key={l.id} className="flex items-center gap-2 bg-slate-50 rounded p-1.5">
                      <Clock className="h-3 w-3 text-slate-400" />
                      <Badge variant="outline" className="text-[9px]">{l.action}</Badge>
                      <span>{l.actor_email}</span>
                      <span className="text-slate-400 ml-auto">{l.at ? new Date(l.at).toLocaleString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CaseDetailDialog = ({ tenantId, id, onClose }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['sg-case-detail', id],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/cases/${id}`)).data,
  });
  const [status, setStatus] = useState('');
  const [actionNote, setActionNote] = useState('');
  const updateMut = useMutation({
    mutationFn: (data) => api.put(`${BASE(tenantId)}/cases/${id}`, data),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['sg-case-detail', id] }); qc.invalidateQueries({ queryKey: ['sg-cases', tenantId] }); setActionNote(''); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {isLoading ? <p>Loading...</p> : (
          <>
            <DialogHeader>
              <DialogTitle>{data?.title}</DialogTitle>
              <DialogDescription className="font-mono text-xs">{data?.number}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline" className={`capitalize ${RISK_COLORS[data?.risk_level]}`}>{data?.risk_level} risk</Badge>
                <Badge variant="outline" className={`capitalize ${STATUS_COLORS[data?.status]}`}>{data?.status}</Badge>
              </div>
              <div className="bg-slate-50 rounded p-3 text-sm whitespace-pre-wrap">{data?.summary}</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <p><b>Owner:</b> {data?.owner_email || '—'}</p>
                <p><b>Review date:</b> {data?.review_date ? new Date(data.review_date).toLocaleDateString() : '—'}</p>
              </div>

              <div className="border-t pt-3">
                <Label>Update status / add action</Label>
                <div className="space-y-2 mt-1">
                  <div className="flex gap-2">
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select status" /></SelectTrigger>
                      <SelectContent>
                        {['open', 'under_review', 'investigation', 'closed', 'escalated'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={() => updateMut.mutate({ status })}
                            disabled={!status || updateMut.isPending}
                            variant="outline">Apply Status</Button>
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Action note to append" value={actionNote} onChange={e => setActionNote(e.target.value)} />
                    <Button onClick={() => updateMut.mutate({ action_note: actionNote })}
                            disabled={!actionNote || updateMut.isPending}
                            className="bg-indigo-600 text-white" data-testid="sg-case-action-submit">Add Action</Button>
                  </div>
                </div>
              </div>

              <div className="border-t pt-3">
                <h4 className="text-sm font-semibold">Actions ({(data?.actions || []).length})</h4>
                <div className="space-y-1 text-xs mt-2">
                  {(data?.actions || []).map((a, i) => (
                    <div key={i} className="bg-slate-50 rounded p-2">
                      <p className="font-semibold">{a.by}</p>
                      <p className="text-slate-600">{a.note}</p>
                      <p className="text-slate-400 text-[10px]">{a.at ? new Date(a.at).toLocaleString() : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SchoolSafeguarding;
